import { useState, type ReactNode } from "react";
import { Loader2, Trash2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import { ApiError } from "../../api/client";
import { qk } from "../../hooks/queries";
import { purgeFieldLocalData } from "../../lib/fieldLocalData";
import { useDataSources } from "../../stores/dataSources";
import { useUiStore } from "../../stores/uiStore";
import type { DashboardField } from "../../types/contract";
import { Modal } from "../../ui/Modal";
import { errMsg } from "../../components/FieldActionData";

// A combined field refuses the first delete. The server names the fields that
// would go with it; the dialog asks before a second call confirms them.
type MergedConfirm = {
  field: DashboardField;
  message: string;
  also: { field_id: string; name: string }[];
};

function alsoRemoved(details: ApiError["details"]): MergedConfirm["also"] {
  return (details ?? [])
    .map((d) => ({
      field_id: String((d as { field_id?: unknown }).field_id ?? ""),
      name: String((d as { name?: unknown }).name ?? ""),
    }))
    .filter((d) => d.field_id);
}

function failureLine(name: string, error: unknown): string {
  const message = errMsg(error);
  // A rolled-back API answers 405. Say why the field was left intact.
  const unavailable = /method not allowed|not implemented|405/i.test(message);
  const why = unavailable
    ? "the server can’t delete fields yet — the backend endpoint is unavailable (it may be mid-deploy); the field was left intact"
    : message;
  return `${name}: ${why}`;
}

// One field. A hard refusal (ERR_DELETE_REFUSED) is a failure, never a second button.
async function deleteOneField(
  field: DashboardField,
  confirmed: Set<string>,
  purgeSourceOverrides: (fieldId: string) => void,
): Promise<"ok" | { confirm: MergedConfirm } | { fail: string }> {
  try {
    await api.deleteField(field.field_id, {
      confirmMerged: confirmed.has(field.field_id),
    });
    // One list owns the per-field browser traces, so a new key is not forgotten here.
    purgeFieldLocalData(field.field_id);
    purgeSourceOverrides(field.field_id);
    const ui = useUiStore.getState();
    if (ui.mapOpen && ui.mapFieldIds.includes(field.field_id)) ui.closeMap();
    return "ok";
  } catch (error) {
    if (error instanceof ApiError && error.code === "ERR_CONFIRM_REQUIRED") {
      return {
        confirm: {
          field,
          message: error.message,
          also: alsoRemoved(error.details),
        },
      };
    }
    return { fail: failureLine(field.field_name, error) };
  }
}

async function deleteFields(
  fields: DashboardField[],
  confirmed: Set<string>,
  purgeSourceOverrides: (fieldId: string) => void,
): Promise<{ failed: string[]; needConfirm: MergedConfirm[] }> {
  const failed: string[] = [];
  const needConfirm: MergedConfirm[] = [];
  for (const field of fields) {
    const result = await deleteOneField(field, confirmed, purgeSourceOverrides);
    if (result === "ok") continue;
    if ("confirm" in result) needConfirm.push(result.confirm);
    else failed.push(result.fail);
  }
  return { failed, needConfirm };
}

function NameList({ names }: { names: string[] }) {
  if (names.length <= 1) return null;
  return (
    <ul className="max-h-28 overflow-auto text-[11px] text-faint">
      {names.slice(0, 8).map((name) => (
        <li key={name}>• {name}</li>
      ))}
      {names.length > 8 && <li>…and {names.length - 8} more</li>}
    </ul>
  );
}

function FailureList({ errs }: { errs: string[] }) {
  if (errs.length === 0) return null;
  return (
    <div className="text-[11px] text-red-400">
      Some deletes failed (the rest went through):
      <ul className="ml-3 mt-0.5 list-disc">
        {errs.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </div>
  );
}

function alsoNames(also: MergedConfirm["also"]): string {
  const shown = also
    .slice(0, 6)
    .map((row) => row.name)
    .join(", ");
  const more = also.length > 6 ? ` +${also.length - 6} more` : "";
  return `${shown}${more}`;
}

function MergedFieldNotice({ items }: { items: MergedConfirm[] }) {
  if (items.length === 0) return null;
  const one = items.length === 1;
  return (
    <div
      role="alertdialog"
      className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px]"
    >
      <p className="font-medium text-fg">
        {one
          ? "This is a combined field"
          : `${items.length} of these are combined fields`}{" "}
        — deleting also removes the fields combined into {one ? "it" : "them"}:
      </p>
      <ul className="ml-3 mt-1 list-disc text-[11px] text-muted">
        {items.map((item) => (
          <li key={item.field.field_id}>
            <span className="text-fg">{item.field.field_name}</span>
            {item.also.length > 0 && (
              <> — also removes {alsoNames(item.also)}</>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-1 text-[11px] text-muted">
        The combine cannot be undone by deleting — those fields’ data lives on
        the combined field and goes with it.
      </p>
    </div>
  );
}

function DeleteActions({
  busy,
  fields,
  merged,
  onDelete,
  onConfirmMerged,
  onClose,
}: {
  busy: boolean;
  fields: DashboardField[];
  merged: MergedConfirm[];
  onDelete: () => void;
  onConfirmMerged: () => void;
  onClose: () => void;
}) {
  const count = merged.reduce((n, item) => n + 1 + item.also.length, 0);
  return (
    <div className="flex items-center gap-2">
      {merged.length > 0 ? (
        <button
          disabled={busy}
          onClick={onConfirmMerged}
          className="flex items-center gap-1.5 rounded bg-red-500 px-3 py-1.5 font-medium text-white hover:bg-red-600 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Trash2 size={13} />
          )}{" "}
          Delete all {count} fields
        </button>
      ) : (
        <button
          disabled={busy}
          onClick={onDelete}
          className="flex items-center gap-1.5 rounded bg-red-500 px-3 py-1.5 font-medium text-white hover:bg-red-600 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Trash2 size={13} />
          )}{" "}
          Delete {fields.length === 1 ? "field" : `${fields.length} fields`}
        </button>
      )}
      <button
        className="text-[11px] text-muted hover:text-fg disabled:opacity-50"
        disabled={busy}
        onClick={onClose}
      >
        Cancel
      </button>
    </div>
  );
}

function ConfirmFrame({
  title,
  busy,
  onClose,
  children,
}: {
  title: string;
  busy: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-dialog flex items-center justify-center bg-black/40 p-4"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="flex w-full max-w-md flex-col rounded-lg bg-surface text-fg shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-3 py-2">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded p-1 hover:bg-hover disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-col gap-3 p-4 text-[12px]">{children}</div>
      </div>
    </div>
  );
}

function DeleteCopy({ count }: { count: number }) {
  const one = count === 1;
  return (
    <p className="text-muted">
      This{" "}
      <span className="font-semibold text-red-400">permanently deletes</span>{" "}
      {one ? "the field" : "these fields"} and{" "}
      <span className="font-semibold">ALL {one ? "its" : "their"} data</span> —
      boundary, soil samples, Haney tests, zones, saved maps and covariates,
      prescriptions, work orders, and machine data. It cannot be undone.
    </p>
  );
}

export function DeleteFieldsConfirm({
  fields,
  onClose,
}: {
  fields: DashboardField[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const purgeSourceOverrides = useDataSources((s) => s.purgeField);
  const [busy, setBusy] = useState(false);
  const [errs, setErrs] = useState<string[]>([]);
  // The list is what the server sent — chained merges included — never something
  // the screen re-derives.
  const [mergedConfirm, setMergedConfirm] = useState<MergedConfirm[]>([]);
  const names = fields.map((field) => field.field_name);

  async function run(confirmed: Set<string> = new Set()) {
    setBusy(true);
    setErrs([]);
    const { failed, needConfirm } = await deleteFields(
      fields,
      confirmed,
      purgeSourceOverrides,
    );
    await qc.invalidateQueries({ queryKey: qk.fields });
    void qc.invalidateQueries({ queryKey: qk.folders });
    setBusy(false);
    if (needConfirm.length) {
      setMergedConfirm(needConfirm);
      if (failed.length) setErrs(failed);
      return;
    }
    if (failed.length) setErrs(failed);
    else onClose();
  }

  const title =
    fields.length === 1
      ? `Delete “${names[0]}”?`
      : `Delete ${fields.length} fields?`;

  return (
    <Modal>
      <ConfirmFrame title={title} busy={busy} onClose={onClose}>
        <DeleteCopy count={fields.length} />
        <NameList names={names} />
        <FailureList errs={errs} />
        <MergedFieldNotice items={mergedConfirm} />
        <DeleteActions
          busy={busy}
          fields={fields}
          merged={mergedConfirm}
          onDelete={() => void run()}
          onConfirmMerged={() => {
            const ok = new Set(
              mergedConfirm.map((item) => item.field.field_id),
            );
            setMergedConfirm([]);
            void run(ok);
          }}
          onClose={onClose}
        />
      </ConfirmFrame>
    </Modal>
  );
}
